import { useEffect, useRef } from 'react';
import c3 from 'c3';
import 'c3/c3.css';
import { useTranslation } from 'react-i18next';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';
import { OniResponse, WeatherChartsResponse } from 'base/types/FieldConditions';
import { getBrazilCropSeasonMonths } from 'utils/brazilCropSeason';
import useMediaQuery from 'hooks/useMediaQuery';
import rainfallIcon from 'assets/icons/conditions-rainfall.svg';
import tooltipMarker from 'assets/icons/conditions-tooltip-marker.svg';
import tooltipDot from 'assets/icons/conditions-tooltip-dot.svg';
import seasonWindowIcon from 'assets/icons/conditions-season-window.svg';
import {
  ChartLegend,
  ChartScroll,
  EnsoLegendLine,
  MobileChartLabel,
} from './FieldConditions.styles';
import { phaseMonthlyRainfall, yearlyRainfall } from './weatherSeries';
import { phaseLabels, phaseIcons, getEnsoLineStyle, EnsoLineTone } from './conditionsTheme';

export { phaseLabels } from './conditionsTheme';

interface TooltipValue {
  label: string;
  icons?: readonly string[];
  tone?: EnsoLineTone;
}

function tooltipValue({ label, icons, tone }: TooltipValue): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'conditions-tooltip-value';
  const marker = document.createElement('span');
  marker.className = tone
    ? `conditions-tooltip-phase-marker conditions-tooltip-phase-${tone}`
    : 'conditions-tooltip-marker';
  icons?.forEach((src) => {
    const image = document.createElement('img');
    image.src = src;
    image.alt = '';
    marker.append(image);
  });
  const text = document.createElement('span');
  text.textContent = label;
  row.append(marker, text);
  return row;
}

function rainfallTooltip(title: string, values: TooltipValue[]): string {
  const tooltip = document.createElement('div');
  tooltip.className = 'conditions-tooltip';
  const heading = document.createElement('div');
  heading.className = 'conditions-tooltip-title';
  heading.textContent = title;
  tooltip.append(heading, ...values.map(tooltipValue));
  return tooltip.outerHTML;
}

interface ConditionsChartProps {
  weather: WeatherChartsResponse;
  oni: OniResponse | null;
  mode: 'monthly' | 'yearly' | 'temperature';
  seasonName?: string;
}

export default function ConditionsChart({ weather, oni, mode, seasonName }: ConditionsChartProps) {
  const container = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width: 600px)');
  const yearly = yearlyRainfall(weather, oni);
  const monthly = phaseMonthlyRainfall(weather, oni);
  const hasPhaseHistory = monthly.some((group) =>
    group.values.some((point) => point.rainfall !== null)
  );
  const rows =
    mode === 'yearly'
      ? [{ label: t('Rainfall'), values: yearly.map((point) => point.rainfall) }]
      : mode === 'temperature'
        ? [{ label: t('Temperature'), values: weather.temperature.lineChart }]
        : hasPhaseHistory
          ? monthly.map((group) => ({
            label: t(phaseLabels[group.phase]),
            values: group.values.map((point) => point.rainfall),
          }))
          : [{ label: t('Historical average'), values: weather.precipitation.monthlyRainfall }];
  const hasData = rows.some((row) => row.values.some((value) => value !== null));
  const maximumRainfall = Math.max(
    50,
    Math.ceil(
      Math.max(
        0,
        ...rows.flatMap((row) =>
          row.values.filter(
            (value): value is number => typeof value === 'number' && Number.isFinite(value)
          )
        )
      ) / 50
    ) * 50
  );
  const rainfallIntervalCount = maximumRainfall / 50;
  const rainfallPlotHeight = rainfallIntervalCount * 24;
  const baseHeight = mobile ? (mode === 'yearly' ? 280 : 220) : 312;
  const chartHeight =
    mode === 'temperature'
      ? baseHeight
      : Math.max(baseHeight, rainfallPlotHeight + (mobile ? 48 : 72));

  useEffect(() => {
    if (!container.current || !hasData) {
      return;
    }
    const chartContainer = container.current;
    let chartWidth = chartContainer.clientWidth;
    const rainfallTicks = Array.from(
      { length: rainfallIntervalCount + 1 },
      (_, index) => index * 50
    );
    const history = yearlyRainfall(weather, oni);
    const chartCategories =
      mode === 'yearly'
        ? history.map((point) => point.label)
        : weather.labels.map((label) => t(label));
    const displayLabels = mobile
      ? chartCategories.map((label) => (mode === 'yearly' ? label.slice(-2) : label.slice(0, 3)))
      : chartCategories;
    const labelCharacterWidth = mobile ? (mode === 'yearly' ? 4 : 5) : 7;
    const longestLabelWidth =
      Math.max(0, ...displayLabels.map((label) => label.length)) * labelCharacterWidth;
    const rotationForWidth = (): number => {
      if (chartWidth <= 0) {
        return 0;
      }
      const plotWidth = Math.max(0, chartWidth - (mobile ? 60 : mode === 'temperature' ? 100 : 108));
      const slotWidth = plotWidth / Math.max(1, chartCategories.length);
      return slotWidth < longestLabelWidth + (mobile ? 1 : 4) ? -90 : 0;
    };
    const groups = phaseMonthlyRainfall(weather, oni);
    const phaseStyles = {
      el_nino: getEnsoLineStyle('el_nino', oni?.impact),
      la_nina: getEnsoLineStyle('la_nina', oni?.impact),
      neutral: getEnsoLineStyle('neutral', oni?.impact),
    };
    const seasonMonths = getBrazilCropSeasonMonths(seasonName) ?? weather.season.months;
    const columns: c3.Data['columns'] =
      mode === 'yearly'
        ? [['rainfall', ...history.map((point) => point.rainfall)]]
        : mode === 'temperature'
          ? [['temperature', ...weather.temperature.lineChart]]
          : hasPhaseHistory
            ? groups.map((group) => [group.phase, ...group.values.map((point) => point.rainfall)])
            : [['rainfall', ...weather.precipitation.monthlyRainfall]];
    const createChart = (rotation: number): c3.ChartAPI => {
      const baseAxisHeight = mobile ? 24 : 40;
      const axisHeight =
        rotation === 0 ? baseAxisHeight : Math.max(baseAxisHeight, longestLabelWidth + 16);
      const height = chartHeight + (axisHeight - baseAxisHeight);
      return c3.generate({
        bindto: chartContainer,
        size: { height, width: chartWidth > 0 ? chartWidth : undefined },
        padding: mobile
          ? { top: 8, right: 8, bottom: 8, left: 52 }
          : { top: 20, right: 16, bottom: 24, left: mode === 'temperature' ? 84 : 92 },
        data: {
          columns,
          type: 'line',
          names: {
            rainfall: t('Rainfall'),
            temperature: t('Temperature'),
            el_nino: t(phaseLabels.el_nino),
            la_nina: t(phaseLabels.la_nina),
            neutral: t('Neutral'),
          },
          colors: {
            rainfall: colors.green50,
            temperature: colors.blue60,
            el_nino: phaseStyles.el_nino.color,
            la_nina: phaseStyles.la_nina.color,
            neutral: phaseStyles.neutral.color,
          },
          classes:
            mode === 'monthly'
              ? Object.fromEntries(
                Object.entries(phaseStyles).map(([phase, style]) => [
                  phase,
                  style.expected
                    ? 'enso-expected'
                    : style.dotted
                      ? 'enso-dotted'
                      : 'enso-comparison',
                ])
              )
              : {},
        },
        axis: {
          x: {
            type: 'category',
            categories: chartCategories,
            height: axisHeight,
            tick: {
              culling: false,
              multiline: false,
              rotate: rotation,
              outer: false,
              values: chartCategories.map((_, index) => index),
              ...(mobile ? { format: (index: number | Date) => displayLabels[Number(index)] } : {}),
            },
          },
          y: {
            label: mobile
              ? undefined
              : {
                text: t(mode === 'temperature' ? 'Temperature' : 'Rainfall'),
                position: 'outer-middle',
              },
            min: mode === 'temperature' ? undefined : 0,
            max: mode === 'temperature' ? undefined : maximumRainfall,
            padding: mode === 'temperature' ? { bottom: 0 } : { top: 0, bottom: 0 },
            tick: {
              values: mode === 'temperature' ? undefined : rainfallTicks,
              format: (value) => `${Math.round(value)}${mode === 'temperature' ? '°C' : 'mm'}`,
            },
          },
        },
        regions:
          mode === 'yearly'
            ? history.map((point, index) => ({
              axis: 'x',
              start: index - 0.46,
              end: index + 0.46,
              class: `phase-${point.phase}`,
              opacity: 1,
            }))
            : mode === 'monthly'
              ? seasonMonths.map((month) => ({
                axis: 'x',
                start: month - 1.48,
                end: month - 0.52,
                class: 'planting-window',
                opacity: 1,
              }))
              : [],
        point: {
          r: mode === 'yearly' && !mobile ? 7.5 : 0,
          focus: { expand: { r: mobile ? 3 : 8.5 } },
        },
        line: { connectNull: false },
        grid: { x: { show: true } },
        legend: { show: false },
        tooltip: {
          ...(mode === 'yearly'
            ? {
              contents: (points: c3.DataPoint[]) => {
                const datum = points[0];
                const point = datum && history[datum.index];
                if (!point) {
                  return '';
                }
                return rainfallTooltip(`${weather.season.label} - ${point.label}`, [
                  {
                    label: `${t(phaseLabels[point.phase])} ${t('Year')} - ${Number(
                      datum.value
                    ).toLocaleString(undefined, { maximumFractionDigits: 1 })} mm`,
                    icons: [tooltipMarker, tooltipDot],
                  },
                ]);
              },
            }
            : mode === 'monthly' && hasPhaseHistory
              ? {
                contents: (points: c3.DataPoint[]) => {
                  const month = weather.labels[points[0]?.index];
                  if (!month) {
                    return '';
                  }
                  const values = (['el_nino', 'neutral', 'la_nina'] as const).flatMap((phase) => {
                    const point = points.find((entry) => entry.id === phase);
                    if (!point || point.value === null || !Number.isFinite(point.value)) {
                      return [];
                    }
                    const label = phaseStyles[phase].expected
                      ? t('{{phase}} (Expected)', { phase: t(phaseLabels[phase]) })
                      : t(phaseLabels[phase]);
                    return [
                      {
                        label: `${label} - ${Number(point.value).toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}mm`,
                        tone: phaseStyles[phase].tone,
                      },
                    ];
                  });
                  return rainfallTooltip(
                    t('{{month}} - average rainfall', { month: t(month) }),
                    values
                  );
                },
              }
              : {}),
          format: {
            title: (index) => {
              const position = Number(index);
              return mode === 'yearly'
                ? `${history[position]?.label} · ${t(
                  phaseLabels[history[position]?.phase ?? 'unknown']
                )}`
                : t(weather.labels[position]);
            },
            value: (value) => `${Number(value).toFixed(1)} ${mode === 'temperature' ? '°C' : 'mm'}`,
          },
        },
        transition: { duration: 0 },
      });
    };
    let rotation = rotationForWidth();
    let chart = createChart(rotation);
    const observer = new ResizeObserver(() => {
      const width = chartContainer.clientWidth;
      if (width > 0 && width !== chartWidth) {
        chartWidth = width;
        const nextRotation = rotationForWidth();
        if (nextRotation !== rotation) {
          chart.destroy();
          rotation = nextRotation;
          chart = createChart(rotation);
        } else {
          chart.resize({ width });
        }
      }
    });
    observer.observe(chartContainer);
    return () => {
      observer.disconnect();
      chart.destroy();
    };
  }, [
    weather,
    oni,
    mode,
    t,
    hasData,
    hasPhaseHistory,
    chartHeight,
    maximumRainfall,
    rainfallIntervalCount,
    seasonName,
    mobile,
  ]);

  if (!hasData) {
    return <p role="status">{t('No historical observations available')}</p>;
  }
  return (
    <>
      <MobileChartLabel>{t(mode === 'temperature' ? 'Temperature' : 'Rainfall')}</MobileChartLabel>
      <ChartScroll
        role="img"
        aria-label={t(mode === 'temperature' ? 'Historical temperature' : 'Historical rainfall')}
      >
        <div className="conditions-chart" data-mode={mode} ref={container} />
      </ChartScroll>
      {mode !== 'temperature' && (
        <ChartLegend>
          {mode === 'yearly' && (
            <span>
              <img src={rainfallIcon} alt="" />
              {t('{{count}} years', { count: yearly.length })}
            </span>
          )}
          {mode === 'monthly' && !hasPhaseHistory && (
            <span>
              <img src={rainfallIcon} alt="" />
              {t('Historical average')}
            </span>
          )}
          {(mode === 'yearly' || hasPhaseHistory) &&
            (mode === 'yearly'
              ? (['el_nino', 'la_nina', 'neutral'] as const)
              : (['la_nina', 'neutral', 'el_nino'] as const)
            ).map((phase) => {
              const style = getEnsoLineStyle(phase, oni?.impact);
              return (
                <span key={phase}>
                  {mode === 'yearly' ? (
                    <img src={phaseIcons[phase]} alt="" />
                  ) : (
                    <EnsoLegendLine
                      $color={style.color}
                      $dotted={style.dotted}
                      aria-hidden="true"
                    />
                  )}
                  {t(phaseLabels[phase])}
                </span>
              );
            })}
          {mode === 'monthly' && (
            <span>
              <img src={seasonWindowIcon} alt="" />
              {seasonName
                ? mobile
                  ? t(seasonName)
                  : t('{{season}} Window', { season: t(seasonName) })
                : t('Planting / harvest window')}
            </span>
          )}
          {mode === 'yearly' && yearly.some((point) => point.phase === 'unknown') && (
            <span>{t('ENSO unavailable')}</span>
          )}
        </ChartLegend>
      )}
    </>
  );
}
